import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePrivy } from '@privy-io/react-auth';
import { useFlowstack } from '@flowstack/sdk';
import Button from '@/components/ui/Button';

interface PrivyLoginButtonProps {
  label?: string;
  className?: string;
}

export default function PrivyLoginButton({ label = 'Sign in', className }: PrivyLoginButtonProps) {
  const navigate = useNavigate();
  const { setCredentials, config } = useFlowstack();
  const { login, logout: privyLogout, authenticated, user, ready, getAccessToken } = usePrivy();
  const verifying = useRef(false);
  const [error, setError] = useState<string | null>(null);
  // Bumped by handleClick to force the verify effect to re-run when the
  // user clicks Sign in on a sticky Privy session. Without this, login()
  // no-ops and the useEffect never re-fires.
  const [clickTick, setClickTick] = useState(0);
  const [signingIn, setSigningIn] = useState(false);

  // P0-72: support `?force_privy_modal=1` to defeat sticky-session silent
  // re-auth when the user clicks Switch Account. Clear Privy on mount so
  // the next login() call actually shows the modal instead of no-op'ing.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('force_privy_modal') !== '1') return;
    // Strip the query param so a refresh doesn't re-trigger the clear.
    const cleanUrl = window.location.pathname + window.location.hash;
    window.history.replaceState({}, '', cleanUrl);
    (async () => {
      try {
        await privyLogout();
      } catch {}
    })();
  }, [privyLogout]);

  // P0-72: handle already-authenticated sticky sessions so clicking Sign in
  // nudges the verify effect instead of calling login() (no-op when
  // authenticated) and leaving the user with no feedback.
  const handleClick = useCallback(() => {
    console.log('[PrivyLogin] click, ready:', ready, 'authenticated:', authenticated);
    setError(null);
    setSigningIn(true);
    if (!ready) {
      setError('Still initializing, try again in a moment.');
      setSigningIn(false);
      return;
    }
    sessionStorage.setItem('privy:login_attempted', '1');
    if (authenticated && user) {
      // Sticky session: login() is a no-op. Bump clickTick so the verify
      // effect re-runs and POSTs the existing Privy token to the backend.
      verifying.current = false;
      setClickTick(t => t + 1);
      return;
    }
    login();
  }, [login, ready, authenticated, user]);

  // After Privy auth, send the Privy access token to /auth/privy-verify
  useEffect(() => {
    const attempted = sessionStorage.getItem('privy:login_attempted');
    if (!authenticated || !user || !attempted || verifying.current) return;
    verifying.current = true;
    sessionStorage.removeItem('privy:login_attempted');

    const verify = async () => {
      try {
        const accessToken = await getAccessToken();
        if (!accessToken) throw new Error('Could not get Privy access token');

        const baseUrl = config.baseUrl || 'https://sage-api.flowstack.fun';
        console.log('[PrivyLogin] sending privy token to backend');

        const res = await fetch(`${baseUrl}/auth/privy-verify`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ privy_token: accessToken }),
        });

        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.detail || 'Backend verification failed');
        }

        const data = await res.json();
        console.log('[PrivyLogin] verified, navigating to workspace');
        setCredentials({
          apiKey: data.session_token,
          tenantId: data.tenant_id || config.tenantId || '',
          userId: data.user_id,
        });

        const next = sessionStorage.getItem('casino:login_next');
        if (next) sessionStorage.removeItem('casino:login_next');
        navigate(next || '/chat');
      } catch (e) {
        console.error('[PrivyLogin] verify failed:', e);
        const msg = e instanceof Error ? e.message : 'Login failed';
        verifying.current = false;
        // P0-72 fix: if the stale Privy session can't produce a valid access
        // token, clear it and immediately re-prompt login so the user gets
        // the Privy modal instead of being stuck on an unrecoverable error.
        // Covers the "authenticated===true but getAccessToken() returns null"
        // case that happens when the Privy refresh token has expired.
        const isStaleSession = /access token|privy token|Not authenticated|401/i.test(msg);
        if (isStaleSession) {
          setError('Session expired — opening sign-in…');
          try { await privyLogout(); } catch {}
          // Clear any residue in flowstack storage too
          sessionStorage.removeItem('privy:login_attempted');
          // Small delay so the user sees the message, then re-prompt
          setTimeout(() => {
            setError(null);
            login();
          }, 400);
          return;
        }
        setError(msg);
        setSigningIn(false);
      }
    };

    verify();
  }, [authenticated, user, config, setCredentials, navigate, getAccessToken, clickTick, privyLogout, login]);

  return (
    <div className={className}>
      <Button
        type="button"
        loading={!ready || signingIn}
        size="lg"
        className="w-full"
        onClick={handleClick}
      >
        {signingIn ? 'Signing in…' : label}
      </Button>
      {error && (
        <p className="text-sm text-[var(--color-error)] bg-[var(--color-error-light)] px-3 py-2 rounded-lg mt-2">
          {error}
        </p>
      )}
    </div>
  );
}
